import React, { lazy } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import Layout from "./components/Layout.jsx";
import RequireAuth from "./features/auth/RequireAuth";
import PersistLogin from "./features/auth/PersistLogin.jsx";
import LoginPage from "./components/mui/LoginPage-mui.jsx";
import LoginOrtu from "./components/LoginOrtu.jsx";

// Lazy Load Components
const DashboardOrtu = lazy(
  () => import("./components/DashboardOrtu/DashboardOrtu.jsx"),
);
const DashboardDataOrtu = lazy(
  () => import("./components/DashboardOrtu/DashboardDataOrtu.jsx"),
);
const RiwayatRaporOrtu = lazy(
  () => import("./components/DashboardOrtu/RiwayatRaporOrtu.jsx"),
);
const NilaiHarianOrtu = lazy(
  () => import("./components/DashboardOrtu/NilaiHarianOrtu.jsx"),
);
const BukuTabunganOrtu = lazy(
  () => import("./components/DashboardOrtu/BukuTabunganOrtu.jsx"),
);
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard.jsx"));
const IsiRaport = lazy(() => import("./components/IsiRaport/IsiRaport.jsx"));
const HasilRaport = lazy(
  () => import("./components/HasilRaport/HasilRaport.jsx"),
);
const DetailRaport = lazy(
  () => import("./components/HasilRaport/DetailRaport.jsx"),
);
const HasilRaportPaud = lazy(
  () => import("./components/HasilRaportPaud/HasilRaportPaud.jsx"),
);
const DetailRaportPaud = lazy(
  () => import("./components/HasilRaportPaud/DetailRaportPaud.jsx"),
);
const MapelDetail = lazy(
  () => import("./components/MapelDetail/MapelDetail.jsx"),
);
const MonitorRaport = lazy(
  () => import("./components/MonitorRaport/MonitorRaport.jsx"),
);
const DaftarPengisianNilai = lazy(
  () => import("./components/MonitorRaport/DaftarPengisianNilai.jsx"),
);
const WakurHasilRaport = lazy(
  () => import("./components/MonitorRaport/WakurHasilRaport.jsx"),
);
const WakurHasilRaportTengah = lazy(
  () => import("./components/MonitorRaport/WakurHasilRaportTengah.jsx"),
);
const WakurRekapNilai = lazy(
  () => import("./components/MonitorRaport/WakurRekapNilai.jsx"),
);
const WakurRekapNilaiTengah = lazy(
  () => import("./components/MonitorRaport/WakurRekapNilaiTengah.jsx"),
);
const HasilRaportTengah = lazy(
  () => import("./components/HasilRaport/HasilRaportTengah.jsx"),
);
const RaporTengahSemester = lazy(
  () => import("./components/HasilRaport/RaporTengahSemester.jsx"),
);
const RekapNilai = lazy(() => import("./components/RekapNilai/RekapNilai.jsx"));
const RekapNilaiTengahSemester = lazy(
  () => import("./components/RekapNilai/RekapNilaiTengahSemester.jsx"),
);
const CreateSiswa = lazy(() => import("./components/Siswa/Siswa.jsx"));
const SiswaList = lazy(() => import("./components/Siswa/SiswaList.jsx"));
const PilihSiswa = lazy(() => import("./components/Siswa/PilihSiswa.jsx"));
const EditSiswa = lazy(() => import("./components/Siswa/EditSiswa.jsx"));
const KelasList = lazy(() => import("./components/Kelas/KelasList.jsx"));
const KelasAdd = lazy(() => import("./components/Kelas/KelasAdd.jsx"));
const KelasEdit = lazy(() => import("./components/Kelas/KelasEdit.jsx"));
const GuruList = lazy(() => import("./components/UserGuru/GuruList.jsx"));
const StatusSppPage = lazy(
  () => import("./components/Bendahara/StatusSppPage.jsx"),
);
const TabunganPage = lazy(
  () => import("./components/Bendahara/TabunganPage.jsx"),
);
const TabunganRekapBulananPage = lazy(
  () => import("./components/Bendahara/TabunganRekapBulananPage.jsx"),
);
const TabunganRekapHarianPage = lazy(
  () => import("./components/Bendahara/TabunganRekapHarianPage.jsx"),
);
const TabunganRekapLaundryPage = lazy(
  () => import("./components/Bendahara/TabunganRekapLaundryPage.jsx"),
);
const TabunganRekapJajanPage = () => <TabunganRekapLaundryPage jenis="jajan" />;
const TabunganSetoranBankPage = lazy(
  () => import("./components/Bendahara/TabunganSetoranBankPage.jsx"),
);
const TabunganLaporanAnakPage = lazy(
  () => import("./components/Bendahara/TabunganLaporanAnakPage.jsx"),
);
const TabunganAlumniPage = lazy(
  () => import("./components/Bendahara/TabunganAlumniPage.jsx"),
);
const TabunganRekapTahunanPage = lazy(
  () => import("./components/Bendahara/TabunganRekapTahunanPage.jsx"),
);
// Buku Tabungan guru/karyawan sementara di-hide; hanya tampil di login wali/ortu.
// const BukuTabunganPage = lazy(
//   () => import("./components/Tabungan/BukuTabunganPage.jsx"),
// );
const GuruAdd = lazy(() => import("./components/UserGuru/GuruAdd.jsx"));
const GuruEdit = lazy(() => import("./components/UserGuru/GuruEdit.jsx"));
const IdentitasSekolahDashboard = lazy(
  () => import("./components/IdentitasSekolah/IdentitasSekolahDashboard.jsx"),
);
const IdentitasSekolah = lazy(
  () => import("./components/IdentitasSekolah/IdentitasSekolah.jsx"),
);
const OrganisasiSekolah = lazy(
  () => import("./components/OrganisasiSekolah/OrganisasiSekolah.jsx"),
);
const EditIdentitasSekolah = lazy(
  () => import("./components/IdentitasSekolah/EditIdentitasSekolah.jsx"),
);
const MataPelajaranList = lazy(
  () => import("./components/MataPelajaran/MataPelajaranList.jsx"),
);
const MapelAdd = lazy(() => import("./components/MataPelajaran/MapelAdd.jsx"));
const MapelEdit = lazy(
  () => import("./components/MataPelajaran/MapelEdit.jsx"),
);
const KelasWakurList = lazy(
  () => import("./components/KelasWakur/KelasWakurList.jsx"),
);
const AddKelasMapelDetail = lazy(
  () => import("./components/KelasWakur/AddKelasMapelDetail.jsx"),
);
const WalasList = lazy(() => import("./components/WalasList/WalasList.jsx"));
const WalasFotoRapor = lazy(
  () => import("./components/WalasFotoRapor/WalasFotoRapor.jsx"),
);
const AddSiswaToKelas = lazy(
  () => import("./components/Siswa/AddSiswaToKelas.jsx"),
);
const ListAbsensi = lazy(() => import("./components/Absensi/ListAbsen.jsx"));
const KoKurikulerList = lazy(
  () => import("./components/Absensi/KoKurikulerList.jsx"),
);
const KoKurikulerPaudList = lazy(
  () => import("./components/Absensi/KoKurikulerPaudList.jsx"),
);
const AspekPaudList = lazy(
  () => import("./components/Absensi/AspekPaudList.jsx"),
);
const SatuanPaudSejenisList = lazy(
  () => import("./components/Absensi/SatuanPaudSejenisList.jsx"),
);
const CapaianPerkembanganPaudList = lazy(
  () => import("./components/Absensi/CapaianPerkembanganPaudList.jsx"),
);
const CapaianBidangPengembanganPaudList = lazy(
  () => import("./components/Absensi/CapaianBidangPengembanganPaudList.jsx"),
);
const ListEkskul = lazy(() => import("./components/Ekskul/ListEkskul.jsx"));
const ListCatatanGuru = lazy(
  () => import("./components/CatatanGuru/ListCatatanGuru.jsx"),
);
const TUList = lazy(() => import("./components/UserTU/TUList.jsx"));
const TUAdd = lazy(() => import("./components/UserTU/TUAdd.jsx"));
const TUEdit = lazy(() => import("./components/UserTU/TUEdit.jsx"));
const CSAccountsPage = lazy(
  () => import("./components/UserCS/CSAccountsPage.jsx"),
);
const LogActivity = lazy(
  () => import("./components/LogActivity/LogActivity.jsx"),
);
const OrtuLoginActivity = lazy(
  () => import("./components/LogActivity/OrtuLoginActivity.jsx"),
);
const MenuActivity = lazy(
  () => import("./components/LogActivity/MenuActivity.jsx"),
);
const AddKelasWalasMapelDetail = lazy(
  () => import("./components/KelasWakur/AddKelasWalasMapelDetail.jsx"),
);
const Profile = lazy(() => import("./components/Profile/Profile.jsx"));
const SchoolList = lazy(() => import("./components/School/SchoolList.jsx"));
const SikapPage = lazy(() => import("./components/Sikap/SikapPage.jsx"));
const KokurikulerSetup = lazy(
  () => import("./components/Kokurikuler/KokurikulerSetup.jsx"),
);

const AcuanEkstrakurikulerPage = lazy(
  () => import("./components/Acuan/AcuanEkstrakurikulerPage.jsx"),
);
const AcuanKokurikulerPage = lazy(
  () => import("./components/Acuan/AcuanKokurikulerPage.jsx"),
);
const AcuanPaudPage = lazy(
  () => import("./components/Acuan/AcuanPaudPage.jsx"),
);
const BidangPengembanganPaudPage = lazy(
  () => import("./components/Paud/BidangPengembanganPaudPage.jsx"),
);
const AcuanKokurikulerPaudPage = lazy(
  () => import("./components/Acuan/AcuanKokurikulerPaudPage.jsx"),
);
const AcuanEkstrakurikulerPaudPage = lazy(
  () => import("./components/Acuan/AcuanEkstrakurikulerPaudPage.jsx"),
);
const AcuanCapaianPaudPage = lazy(
  () => import("./components/Acuan/AcuanCapaianPaudPage.jsx"),
);

const RaportPondok = lazy(
  () => import("./components/RaportPondok/RaportPondok.jsx"),
);

const router = createBrowserRouter([
  {
    path: "/",
    Component: LoginPage,
  },
  {
    path: "/wali",
    Component: LoginOrtu,
  },
  {
    Component: PersistLogin,
    children: [
      {
        Component: RequireAuth,
        children: [
          {
            path: "/dashboard-ortu",
            Component: DashboardOrtu,
            children: [
              {
                index: true,
                Component: DashboardDataOrtu,
              },
              {
                path: "nilai-harian",
                Component: NilaiHarianOrtu,
              },
              {
                path: "buku-tabungan",
                Component: BukuTabunganOrtu,
              },
              {
                path: "rapor-tengah/:siswaId",
                Component: RaporTengahSemester,
              },
              {
                path: "rapor-akhir/:siswaId",
                Component: DetailRaport,
              },
              {
                path: "riwayat-rapor/:siswaId",
                Component: RiwayatRaporOrtu,
              },
              {
                path: "profile",
                Component: Profile,
              },
            ],
          },
          {
            Component: Layout,
            children: [
              {
                path: "/monitor-raport/raport-pondok",
                Component: RaportPondok,
              },
              {
                path: "/raport-pondok",
                Component: RaportPondok,
              },
              {
                path: "/monitor-raport",
                Component: MonitorRaport,
              },
              {
                path: "/monitor-raport/daftar-pengisian",
                Component: DaftarPengisianNilai,
              },
              {
                path: "/monitor-raport/hasil-raport",
                Component: WakurHasilRaport,
              },
              {
                path: "/monitor-raport/hasil-raport-tengah",
                Component: WakurHasilRaportTengah,
              },
              {
                path: "/monitor-raport/rekap-nilai",
                Component: WakurRekapNilai,
              },
              {
                path: "/monitor-raport/rekap-nilai-tengah",
                Component: WakurRekapNilaiTengah,
              },
              {
                path: "/sikap-spiritual",
                element: <SikapPage type="SPIRITUAL" />,
              },
              {
                path: "/sikap-sosial",
                element: <SikapPage type="SOSIAL" />,
              },
              {
                path: "/dashboard",
                Component: Dashboard,
              },
              {
                path: "/buku-tabungan",
                // Buku Tabungan guru/karyawan sementara di-hide; hanya tampil di login wali/ortu.
                element: <Navigate to="/dashboard" replace />,
              },
              {
                path: "/profile",
                Component: Profile,
              },
              {
                path: "/isi-raport/:id",
                Component: IsiRaport,
              },
              {
                path: "/hasil-raport",
                Component: HasilRaport,
              },
              {
                path: "/hasil-raport-paud",
                Component: HasilRaportPaud,
              },
              {
                path: "/hasil-raport/:siswaId",
                Component: DetailRaport,
              },
              {
                path: "/hasil-raport-paud/:siswaId",
                Component: DetailRaportPaud,
              },
              {
                path: "/mapel-detail",
                Component: MapelDetail,
              },
              {
                path: "/raport-tengah-semester",
                Component: HasilRaportTengah,
              },
              {
                path: "/hasil-raport-tengah/:siswaId",
                Component: RaporTengahSemester,
              },
              {
                path: "/rekap-nilai",
                Component: RekapNilai,
              },
              {
                path: "/rekap-tengah-semester",
                Component: RekapNilaiTengahSemester,
              },
              {
                path: "/siswa",
                Component: CreateSiswa,
              },
              {
                path: "/siswa-list",
                Component: SiswaList,
              },
              {
                path: "/pilih-siswa",
                Component: PilihSiswa,
              },
              {
                path: "/edit-siswa/:uuid",
                Component: EditSiswa,
              },
              {
                path: "/kelas",
                Component: KelasList,
              },
              {
                path: "/add-kelas",
                Component: KelasAdd,
              },
              {
                path: "/edit-kelas/:uuid",
                Component: KelasEdit,
              },
              {
                path: "/bendahara/spp",
                Component: StatusSppPage,
              },
              {
                path: "/bendahara/tabungan",
                Component: TabunganPage,
              },
              {
                path: "/bendahara/tabungan/rekap-bulanan",
                Component: TabunganRekapBulananPage,
              },
              {
                path: "/bendahara/tabungan/rekap-harian",
                Component: TabunganRekapHarianPage,
              },
              {
                path: "/bendahara/tabungan/rekap-jajan",
                Component: TabunganRekapJajanPage,
              },
              {
                path: "/bendahara/tabungan/rekap-laundry",
                Component: TabunganRekapLaundryPage,
              },
              {
                path: "/bendahara/tabungan/rekap-tahunan",
                Component: TabunganRekapTahunanPage,
              },
              {
                path: "/bendahara/tabungan/setoran-bank",
                Component: TabunganSetoranBankPage,
              },
              {
                path: "/bendahara/tabungan/laporan-anak",
                Component: TabunganLaporanAnakPage,
              },
              {
                path: "/bendahara/tabungan/alumni",
                Component: TabunganAlumniPage,
              },
              {
                path: "/guru",
                Component: GuruList,
              },
              {
                path: "/add-guru",
                Component: GuruAdd,
              },
              {
                path: "/edit-guru/:uuid",
                Component: GuruEdit,
              },
              {
                path: "/identitas-sekolah",
                Component: IdentitasSekolahDashboard,
              },
              {
                path: "/create-identitas",
                Component: IdentitasSekolah,
              },
              {
                path: "/strucktur-organisasi",
                Component: OrganisasiSekolah,
              },
              {
                path: "/edit-identitas/:uuid",
                Component: EditIdentitasSekolah,
              },
              {
                path: "/mapel",
                Component: MataPelajaranList,
              },
              {
                path: "/mapel-add",
                Component: MapelAdd,
              },
              {
                path: "/edit-mapel/:uuid",
                Component: MapelEdit,
              },
              {
                path: "/kelas-wakur",
                Component: KelasWakurList,
              },
              {
                path: "/kelas-wakur-guru",
                Component: KelasWakurList,
              },
              {
                path: "/kelas-wakur-walas",
                Component: KelasWakurList,
              },
              {
                path: "/mapel-detail-add",
                Component: AddKelasMapelDetail,
              },
              {
                path: "/mapel-detail-add-walas",
                Component: AddKelasWalasMapelDetail,
              },
              {
                path: "/mapel-detail-walas",
                Component: WalasList,
              },
              {
                path: "/foto-rapor-walas",
                Component: WalasFotoRapor,
              },
              {
                path: "/tambah-pilih-siswa",
                Component: AddSiswaToKelas,
              },
              {
                path: "/absensi",
                Component: ListAbsensi,
              },
              {
                path: "/ko-kurikuler",
                Component: KoKurikulerList,
              },
              {
                path: "/aspek",
                Component: AspekPaudList,
              },
              {
                path: "/ko-kurikuler-paud",
                Component: KoKurikulerPaudList,
              },
              {
                path: "/ekstrakulikuler",
                Component: ListEkskul,
              },
              {
                path: "/satuan-paud-sejenis",
                Component: SatuanPaudSejenisList,
              },
              {
                path: "/capaian-perkembangan-paud",
                Component: CapaianPerkembanganPaudList,
              },
              {
                path: "/capaian-bidang-pengembangan-paud",
                Component: CapaianBidangPengembanganPaudList,
              },
              {
                path: "/catatan-wali",
                Component: ListCatatanGuru,
              },
              {
                path: "/user-tu",
                Component: TUList,
              },
              {
                path: "/add-tu",
                Component: TUAdd,
              },
              {
                path: "/edit-tu/:uuid",
                Component: TUEdit,
              },
              {
                path: "/akun-tendik",
                Component: CSAccountsPage,
              },
              {
                path: "/akun-cs-tabungan",
                element: <Navigate to="/akun-tendik" replace />,
              },
              {
                path: "/login-activty",
                Component: LogActivity,
              },
              {
                path: "/ortu-login-activity",
                Component: OrtuLoginActivity,
              },
              {
                path: "/menu-activity",
                Component: MenuActivity,
              },
              {
                path: "/schools",
                Component: SchoolList,
              },
              {
                path: "/kokurikuler-setup",
                Component: KokurikulerSetup,
              },
              {
                path: "/acuan-kokurikuler",
                Component: AcuanKokurikulerPage,
              },
              {
                path: "/acuan-paud",
                Component: AcuanPaudPage,
              },
              {
                path: "/acuan-bidang-pengembangan-paud",
                Component: BidangPengembanganPaudPage,
              },
              {
                path: "/acuan-kokurikuler-paud",
                Component: AcuanKokurikulerPaudPage,
              },
              {
                path: "/acuan-ekstrakurikuler-paud",
                Component: AcuanEkstrakurikulerPaudPage,
              },
              {
                path: "/acuan-capaian-paud",
                Component: AcuanCapaianPaudPage,
              },
              {
                path: "/acuan-ekstrakurikuler",
                Component: AcuanEkstrakurikulerPage,
              },
            ],
          },
        ],
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
